import {useRef} from 'react'
import {useLazyGetPaymentSessionStatusQuery} from '../services/adyenPaymentsApi'
import {getLogger} from '../services/logging'
import ReactGA from 'react-ga4'
import {PaymentSessionStatus} from '../schema'

interface PaymentStatusPollingOptions {
  paymentSessionId: string
  onPaymentSuccess: () => void
  onPaymentFailure: (error?: string) => void
  intervalMs?: number
  maxAttempts?: number
}

// polling interval in ms and max attempts
const DEFAULT_POLL_INTERVAL = 15000
const DEFAULT_POLL_MAX_ATTEMPTS = 10

const logger = getLogger('usePaymentStatusPoller')

export const usePaymentStatusPoller = () => {
  const pollAttempts = useRef(0)
  const [getPaymentSessionStatus] = useLazyGetPaymentSessionStatusQuery()

  const pollForPaymentStatus = async ({
    paymentSessionId,
    onPaymentSuccess,
    onPaymentFailure,
    intervalMs = DEFAULT_POLL_INTERVAL,
    maxAttempts = DEFAULT_POLL_MAX_ATTEMPTS
  }: PaymentStatusPollingOptions): Promise<void> => {
    const intervalId = setInterval(async () => {
      logger.info({
        message: `Polling for payment session status: ${paymentSessionId}`
      })

      if (pollAttempts.current >= maxAttempts) {
        logger.info({
          message: 'Max polling attempts reached, stop polling for status'
        })
        clearInterval(intervalId)
        onPaymentFailure()
        return
      } else {
        pollAttempts.current += 1
      }

      try {
        const response = await getPaymentSessionStatus(paymentSessionId)
        const status = response.data?.status as PaymentSessionStatus
        const error = response.data?.error as string | undefined

        logger.info({
          message: 'Retrieved payment session status',
          data: {status, error}
        })
        switch (status) {
          case PaymentSessionStatus.PaymentSucceeded:
            logger.info({
              message: 'Payment succeeded, stop polling for status'
            })
            ReactGA.event({
              category: 'IBE',
              action: 'Checkout_Payment_Success',
              value: 1
            })
            clearInterval(intervalId)
            onPaymentSuccess()
            break
          case PaymentSessionStatus.PaymentRejected:
          case PaymentSessionStatus.PaymentFailed:
            logger.warn({message: 'Payment failed, stop polling for status'})
            ReactGA.event({
              category: 'IBE',
              action: 'Checkout_Payment_Failure',
              value: 1
            })
            clearInterval(intervalId)
            onPaymentFailure(error)
            break
          default:
            logger.info({message: 'Continue polling for status...'})
            ReactGA.event({
              category: 'IBE',
              action: 'Checkout_Payment_Polling',
              value: 1
            })
        }
      } catch (error) {
        logger.error({
          message:
            'Failed to retrieve payment session status, stop polling for status',
          errorData: {error}
        })
        clearInterval(intervalId)
        onPaymentFailure()
      }
    }, intervalMs)
  }

  return {
    pollForPaymentStatus
  }
}
